import { Contents } from '../modules/editor/types';
import { stringToDom } from './html';

export type LinkEntry = {
	href: string;
	text: string;
	location: string;
};

export type LinkCheckPayload = {
	entries: LinkEntry[];
	text: string;
};

type SnsItem = {
	url?: string;
	label?: string;
	active?: boolean;
};

type BlockContent = {
	text?: string;
	imageLink?: string;
	buttonText?: string;
	buttonLink?: string;
	adLink?: string;
	sns?: SnsItem[];
	product?: { product_url?: string };
};

type EditorBlock = {
	type?: string;
	typeText?: string;
	category?: string;
	column?: string[][];
	content?: Array<BlockContent | BlockContent[]>;
};

const isNonEmpty = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

const linksFromHtml = (html: string | undefined, location: string): LinkEntry[] => {
	if (!isNonEmpty(html)) {
		return [];
	}
	const anchors = Array.from(stringToDom(html).querySelectorAll('a'));
	return anchors.map((anchor) => ({
		href: (anchor.getAttribute('href') ?? '').trim(),
		text: (anchor.textContent ?? '').trim(),
		location
	}));
};

const linksFromBaseColumns = (block: EditorBlock, location: string): LinkEntry[] => {
	const columns = block.column ?? [];
	return columns.flatMap((slot, index) => {
		const item = block.content?.[index];
		if (item == null || Array.isArray(item)) {
			return [];
		}
		return slot.flatMap((element) => {
			if (element === 'image') {
				return isNonEmpty(item.imageLink) ? [{ href: item.imageLink.trim(), text: '', location }] : [];
			}
			if (element === 'cta') {
				return [{ href: (item.buttonLink ?? '').trim(), text: (item.buttonText ?? '').trim(), location }];
			}
			if (element === 'text') {
				return linksFromHtml(item.text, location);
			}
			return [];
		});
	});
};

const linksFromSns = (block: EditorBlock, location: string): LinkEntry[] => {
	const first = block.content?.[0];
	if (first == null || Array.isArray(first) || first.sns == null) {
		return [];
	}
	return first.sns
		.filter((sns) => sns.active && isNonEmpty(sns.url))
		.map((sns) => ({ href: (sns.url as string).trim(), text: sns.label ?? '', location }));
};

const linksFromMap = (block: EditorBlock, location: string): LinkEntry[] => {
	const first = block.content?.[0];
	if (first == null || Array.isArray(first) || !isNonEmpty(first.imageLink)) {
		return [];
	}
	return [{ href: first.imageLink.trim(), text: '', location }];
};

const linksFromHtmlBlock = (block: EditorBlock, location: string): LinkEntry[] => {
	const first = block.content?.[0];
	if (first == null || Array.isArray(first)) {
		return [];
	}
	return linksFromHtml(first.text, location);
};

const flattenItems = (content: EditorBlock['content']): BlockContent[] =>
	(content ?? [])
		.flatMap((item) => (Array.isArray(item) ? item : [item]))
		.filter((item): item is BlockContent => item != null && typeof item === 'object');

const linksFromProductBlock = (block: EditorBlock, location: string): LinkEntry[] =>
	flattenItems(block.content).flatMap((item) => {
		const entries: LinkEntry[] = [];
		if (isNonEmpty(item.imageLink)) {
			entries.push({ href: item.imageLink.trim(), text: '', location });
		}
		if (isNonEmpty(item.product?.product_url)) {
			entries.push({ href: (item.product as { product_url: string }).product_url.trim(), text: '', location });
		}
		if ('buttonLink' in item || 'buttonText' in item) {
			entries.push({ href: (item.buttonLink ?? '').trim(), text: (item.buttonText ?? '').trim(), location });
		}
		entries.push(...linksFromHtml(item.text, location));
		return entries;
	});

const entriesFromBlock = (block: EditorBlock): LinkEntry[] => {
	const location = `${block.typeText ?? block.type ?? '알 수 없는'} 상자`;
	switch (block.category) {
		case 'base':
			return linksFromBaseColumns(block, location);
		case 'sns':
			return linksFromSns(block, location);
		case 'map':
			return linksFromMap(block, location);
		case 'htmlcode':
		case 'footer':
		case 'permalink':
			return linksFromHtmlBlock(block, location);
		case 'shop':
		case 'donation':
			return linksFromProductBlock(block, location);
		default:
			return [];
	}
};

const collectMergeText = (block: EditorBlock): string[] =>
	flattenItems(block.content).flatMap((item) => (isNonEmpty(item.text) ? [item.text] : []));

export function extractLinkCheckPayload(contents: Contents): LinkCheckPayload {
	const blocks = (contents ?? []).map((item) => item.content as EditorBlock);
	const entries = blocks.flatMap(entriesFromBlock);
	const texts = blocks.flatMap(collectMergeText);
	const text = [...texts, ...entries.flatMap((entry) => [entry.text, entry.href])].join('\n');
	return { entries, text };
}
